import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Pencil, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/twilio/admin/crm/respuestas-rapidas`;
const VACIO = { id: null, titulo: '', texto: '' };

export default function RespuestasRapidasConfig({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(VACIO);
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(() => axios.get(BASE, { headers }).then(r => setItems(r.data.items || [])), [headers]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setSaving(true);
    try {
      if (form.id) await axios.put(`${BASE}/${form.id}`, form, { headers });
      else await axios.post(BASE, form, { headers });
      toast.success('Respuesta guardada');
      setForm(VACIO);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
    setSaving(false);
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar esta respuesta rápida?')) return;
    await axios.delete(`${BASE}/${id}`, { headers });
    cargar();
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="respuestas-rapidas-config">
      <Card>
        <CardHeader><CardTitle className="text-base">{form.id ? 'Editar respuesta' : 'Nueva respuesta rápida'}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Título (ej: Saludo)" value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })}
            data-testid="rr-titulo" />
          <Textarea rows={6} placeholder="Texto. Variables: {nombre}, {matricula}" value={form.texto}
            onChange={e => setForm({ ...form, texto: e.target.value })} data-testid="rr-texto" />
          <p className="text-xs text-gray-500">Se comparten con todo el equipo. Variables: <code>{'{nombre}'}</code>, <code>{'{matricula}'}</code>.</p>
          <div className="flex gap-2">
            <Button onClick={guardar} disabled={saving || !form.titulo.trim() || !form.texto.trim()} data-testid="rr-guardar">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />{form.id ? 'Guardar cambios' : 'Agregar'}</>}
            </Button>
            {form.id && <Button variant="outline" onClick={() => setForm(VACIO)} data-testid="rr-cancelar">Cancelar</Button>}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Respuestas guardadas ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-[480px] overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-gray-400">Todavía no hay respuestas.</p>}
          {items.map(i => (
            <div key={i.id} className="border rounded-md p-2.5 flex gap-2" data-testid={`rr-item-${i.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{i.titulo}</p>
                <p className="text-xs text-gray-500 whitespace-pre-wrap line-clamp-3">{i.texto}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setForm(i)} data-testid={`rr-editar-${i.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => borrar(i.id)} data-testid={`rr-borrar-${i.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
